import { useEffect, useMemo, memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  FormFieldDefinition,
  FormSectionDefinition,
  ChoiceFieldDefinition,
  DropdownFieldDefinition,
  CalculatedFieldDefinition,
} from "@rwaforms/shared";
import { nanoid } from "nanoid";
import { useBuilderStore } from "../hooks/useBuilderStore";
import { fetchTeamThemes, setFormTheme } from "../lib/api-client";

type SidebarView = ReturnType<typeof useBuilderStore.getState>["sidebarView"];

// Validation and sanitization functions
function sanitizeInput(input: string): string {
  return input.trim();
}

function validateFieldTitle(title: string): { valid: boolean; error?: string } {
  if (!title || title.length === 0) {
    return { valid: false, error: "Field title cannot be empty" };
  }
  if (title.length > 200) {
    return { valid: false, error: "Field title cannot exceed 200 characters" };
  }
  return { valid: true };
}

function validateFieldDescription(description: string): { valid: boolean; error?: string } {
  if (description.length > 1000) {
    return { valid: false, error: "Field description cannot exceed 1000 characters" };
  }
  return { valid: true };
}

function validateNumberValidation(
  min: number | undefined,
  max: number | undefined
): { valid: boolean; error?: string } {
  if (min !== undefined && max !== undefined && min > max) {
    return { valid: false, error: "Minimum value cannot be greater than maximum value" };
  }
  return { valid: true };
}

function validateFileSizeLimit(size: number): { valid: boolean; error?: string } {
  if (size < 1 || size > 50) {
    return { valid: false, error: "Max file size must be between 1 and 50 MB" };
  }
  return { valid: true };
}

function validateRatingScale(scale: number): { valid: boolean; error?: string } {
  if (scale < 2 || scale > 10) {
    return { valid: false, error: "Rating scale must be between 2 and 10" };
  }
  return { valid: true };
}

function validateChoiceOptions(
  options: Array<{ id: string; label: string }>
): { valid: boolean; error?: string } {
  if (!options || options.length === 0) {
    return { valid: false, error: "At least one option is required" };
  }
  const labels = options.map((opt) => opt.label);
  const uniqueLabels = new Set(labels);
  if (uniqueLabels.size !== labels.length) {
    return { valid: false, error: "Option labels must be unique" };
  }
  return { valid: true };
}

const fieldTypeLabel: Record<string, string> = {
  short_text: "Short answer",
  long_text: "Paragraph",
  number: "Number",
  multiple_choice: "Single choice",
  checkboxes: "Multiple choice",
  dropdown: "Dropdown",
  rating: "Rating",
  file_upload: "File upload",
  date: "Date",
  time: "Time",
  calculated: "Calculated",
  static_text: "Static content",
};

const PropertiesPanel = memo(function PropertiesPanel(): JSX.Element {
  const schema = useBuilderStore((state) => state.schema);
  const selectedFieldId = useBuilderStore((state) => state.selectedFieldId);
  const selectedSectionId = useBuilderStore((state) => state.selectedSectionId);
  const sidebarView = useBuilderStore((state) => state.sidebarView);
  const setSidebar = useBuilderStore((state) => state.setSidebar);
  const teamId = useBuilderStore((state) => state.teamId);
  const formId = useBuilderStore((state) => state.formId);
  const formTheme = useBuilderStore((state) => state.formTheme);
  const teamTheme = useBuilderStore((state) => state.teamTheme);
  const setFormThemeState = useBuilderStore((state) => state.setFormTheme);
  const setTeamThemeState = useBuilderStore((state) => state.setTeamTheme);

  // Memoize field lookup
  const field = useMemo<FormFieldDefinition | undefined>(
    () => schema.fields.find((item) => item.id === selectedFieldId),
    [schema.fields, selectedFieldId]
  );

  // Memoize section lookup
  const section = useMemo<FormSectionDefinition | undefined>(
    () => schema.sections.find((item) => item.id === selectedSectionId),
    [schema.sections, selectedSectionId]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <SidebarTabs active={sidebarView} onChange={setSidebar} />
      <div className="scroll-container" style={{ flex: 1, paddingRight: "0.5rem" }}>
        {sidebarView === "properties" && (
          <>
            <ThemeControls
              teamId={teamId}
              formId={formId}
              formTheme={formTheme}
              teamTheme={teamTheme}
              setFormThemeState={setFormThemeState}
              setTeamThemeState={setTeamThemeState}
            />
            {field ? (
              <FieldProperties field={field} />
            ) : section ? (
              <SectionProperties section={section} />
            ) : (
              <EmptyState />
            )}
          </>
        )}
        {sidebarView === "settings" && <FormSettingsPanel />}
        {sidebarView === "logic" && <Placeholder message="Logic designer coming in Phase 2B." />}
        {sidebarView === "calculations" && (
          <Placeholder message="Calculation composer launches with analytics rollout." />
        )}
      </div>
    </div>
  );
});

function SidebarTabs({
  active,
  onChange,
}: {
  active: SidebarView;
  onChange: (view: SidebarView) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
      {(["properties", "settings", "logic", "calculations"] as SidebarView[]).map((view) => (
        <button
          key={view}
          type="button"
          className={`button ${active === view ? "primary" : "ghost"}`}
          onClick={() => onChange(view)}
          style={{ fontSize: "0.8rem", padding: "0.4rem 0.8rem" }}
        >
          {view === "properties" ? "📝 Fields" : view === "settings" ? "⚙️ Settings" : view === "logic" ? "🔀 Logic" : "🔢 Calc"}
        </button>
      ))}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
      Select a field or section on the canvas to view its configuration. Properties update in real
      time as you type.
    </div>
  );
}

/**
 * Form-level settings panel for availability, limits, and deduplication
 */
function FormSettingsPanel(): JSX.Element {
  const schema = useBuilderStore((state) => state.schema);
  const updateFormSettings = useBuilderStore((state) => state.updateFormSettings);

  const settings = schema.settings;

  const labelStyle = {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 500,
    marginBottom: "0.25rem",
    color: "var(--color-text-muted)",
  };

  const inputStyle = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid var(--color-border)",
    borderRadius: "calc(var(--radius-base) - 2px)",
    fontSize: "0.875rem",
    fontFamily: "inherit",
  };

  const sectionStyle = {
    marginBottom: "1.5rem",
    padding: "1rem",
    background: "var(--color-bg-solid)",
    borderRadius: "var(--radius-base)",
    border: "1px solid var(--color-border)",
  };

  const sectionTitleStyle = {
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>Form Settings</h3>

      {/* Response Limits */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>🔢</span> Response Limits
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label style={labelStyle}>Maximum Responses</label>
          <input
            type="number"
            value={settings.maxResponses ?? ""}
            onChange={(e) => updateFormSettings({
              maxResponses: e.target.value ? parseInt(e.target.value, 10) : undefined,
            })}
            placeholder="Unlimited"
            min={1}
            style={inputStyle}
          />
          <p style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: "0.25rem" }}>
            Leave empty for unlimited responses
          </p>
        </div>
      </div>

      {/* Scheduled Availability */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>📅</span> Scheduled Availability
        </div>
        <div style={{ display: "grid", gap: "1rem" }}>
          <div>
            <label style={labelStyle}>Opens At</label>
            <input
              type="datetime-local"
              value={settings.opensAt ? settings.opensAt.slice(0, 16) : ""}
              onChange={(e) => updateFormSettings({
                opensAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Closes At</label>
            <input
              type="datetime-local"
              value={settings.closesAt ? settings.closesAt.slice(0, 16) : ""}
              onChange={(e) => updateFormSettings({
                closesAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Closed Message</label>
            <textarea
              value={settings.closedMessage ?? ""}
              onChange={(e) => updateFormSettings({
                closedMessage: e.target.value || undefined,
              })}
              placeholder="This form is no longer accepting responses."
              rows={2}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Deduplication */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>
          <span>🔒</span> Response Deduplication
        </div>
        <div style={{ display: "grid", gap: "1rem" }}>
          <div>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={settings.deduplication?.enabled ?? false}
                onChange={(e) => updateFormSettings({
                  deduplication: {
                    ...settings.deduplication,
                    enabled: e.target.checked,
                    mode: settings.deduplication?.mode ?? "ip",
                    behavior: settings.deduplication?.behavior ?? "block",
                  },
                })}
              />
              Enable deduplication
            </label>
          </div>

          {settings.deduplication?.enabled && (
            <>
              <div>
                <label style={labelStyle}>Detection Method</label>
                <select
                  value={settings.deduplication?.mode ?? "ip"}
                  onChange={(e) => updateFormSettings({
                    deduplication: {
                      ...settings.deduplication,
                      enabled: true,
                      mode: e.target.value as "email" | "phone" | "ip" | "device" | "cookie" | "email_or_phone",
                      behavior: settings.deduplication?.behavior ?? "block",
                    },
                  })}
                  style={inputStyle}
                >
                  <option value="ip">IP Address</option>
                  <option value="device">Device ID</option>
                  <option value="cookie">Browser Cookie</option>
                  <option value="email">Email Field</option>
                  <option value="phone">Phone Field</option>
                  <option value="email_or_phone">Email or Phone</option>
                </select>
              </div>
              {["email", "phone", "email_or_phone"].includes(settings.deduplication?.mode ?? "") && (
                <div>
                  <label style={labelStyle}>Detection Field</label>
                  <select
                    value={settings.deduplication?.fieldId ?? ""}
                    onChange={(e) => updateFormSettings({
                      deduplication: {
                        ...settings.deduplication,
                        enabled: true,
                        mode: settings.deduplication?.mode ?? "ip",
                        fieldId: e.target.value || undefined,
                      },
                    })}
                    style={inputStyle}
                  >
                    <option value="">Auto-detect from field type</option>
                    {schema.fields
                      .filter(f => ["email", "phone", "short_text"].includes(f.type))
                      .map(f => (
                        <option key={f.id} value={f.id}>
                          {f.title || f.id}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div>
                <label style={labelStyle}>Duplicate Behavior</label>
                <select
                  value={settings.deduplication?.behavior ?? "block"}
                  onChange={(e) => updateFormSettings({
                    deduplication: {
                      ...settings.deduplication,
                      enabled: true,
                      mode: settings.deduplication?.mode ?? "ip",
                      behavior: e.target.value as "block" | "warn" | "allow",
                    },
                  })}
                  style={inputStyle}
                >
                  <option value="block">Block duplicate submissions</option>
                  <option value="warn">Show warning but allow</option>
                  <option value="allow">Allow and track duplicates</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Duplicate Message</label>
                <textarea
                  value={settings.deduplication?.message ?? ""}
                  onChange={(e) => updateFormSettings({
                    deduplication: {
                      ...settings.deduplication,
                      enabled: true,
                      mode: settings.deduplication?.mode ?? "ip",
                      message: e.target.value || undefined,
                    },
                  })}
                  placeholder="You have already submitted a response."
                  rows={2}
                  style={inputStyle}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ThemeControls({
  teamId,
  formId,
  formTheme,
  teamTheme,
  setFormThemeState,
  setTeamThemeState,
}: {
  teamId: string | null;
  formId: string | null;
  formTheme: ReturnType<typeof useBuilderStore.getState>["formTheme"];
  teamTheme: ReturnType<typeof useBuilderStore.getState>["teamTheme"];
  setFormThemeState(theme: ReturnType<typeof useBuilderStore.getState>["formTheme"]): void;
  setTeamThemeState(theme: ReturnType<typeof useBuilderStore.getState>["teamTheme"]): void;
}): JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const themeQuery = useQuery({
    queryKey: ["team-themes", teamId],
    queryFn: () => fetchTeamThemes(teamId!),
    enabled: Boolean(teamId),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!themeQuery.data) return;
    const active = themeQuery.data.activeThemeId
      ? themeQuery.data.themes.find((theme) => theme.id === themeQuery.data.activeThemeId) ?? null
      : null;
    setTeamThemeState(active);
  }, [themeQuery.data, setTeamThemeState]);

  const setThemeMutation = useMutation({
    mutationFn: async (themeId: string | null) => {
      if (!formId) throw new Error("formId missing");
      await setFormTheme(formId, { themeId });
      return themeId;
    },
    onSuccess(themeId) {
      const selected =
        themeId === null
          ? null
          : themeQuery.data?.themes.find((theme) => theme.id === themeId) ?? null;
      setFormThemeState(selected);
      void queryClient.invalidateQueries({ queryKey: ["form", formId] });
    },
  });

  if (!teamId || !formId) {
    return null;
  }

  const themes = themeQuery.data?.themes ?? [];
  const selectValue = formTheme ? formTheme.id : "";
  const defaultThemeName =
    themeQuery.data?.activeThemeId &&
    themes.find((theme) => theme.id === themeQuery.data.activeThemeId)?.name;
  const resolvedThemeName = formTheme?.name ?? defaultThemeName ?? "Team default";
  const effectiveTheme = formTheme ?? teamTheme ?? null;

  return (
    <div
      style={{
        marginBottom: "1rem",
        padding: "1rem",
        borderRadius: "var(--radius-base)",
        border: "1px solid var(--color-border)",
        background: "rgba(37, 99, 235, 0.04)",
        display: "grid",
        gap: "0.75rem",
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Theme</h3>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
            {themeQuery.isLoading ? "Loading themes…" : `Applied: ${resolvedThemeName}`}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            className="button ghost"
            style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem" }}
            onClick={() => void themeQuery.refetch()}
            disabled={themeQuery.isFetching}
          >
            Refresh
          </button>
          <button
            type="button"
            className="button ghost"
            style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem" }}
            onClick={() => navigate(`/teams/${teamId}/themes`)}
          >
            Theme studio
          </button>
        </div>
      </header>
      <label style={{ display: "flex", flexDirection: "column", gap: "0.4rem", fontSize: "0.85rem" }}>
        <span>Choose theme</span>
        <select
          value={selectValue}
          onChange={(event) => {
            const nextValue = event.target.value || null;
            setThemeMutation.mutate(nextValue);
          }}
          style={{
            borderRadius: "10px",
            border: "1px solid var(--color-border)",
            padding: "0.55rem 0.75rem",
            fontSize: "0.9rem",
          }}
          disabled={themeQuery.isLoading || setThemeMutation.isPending}
        >
          <option value="">
            Use team default{defaultThemeName ? ` (${defaultThemeName})` : ""}
          </option>
          {themes.map((theme) => (
            <option key={theme.id} value={theme.id}>
              {theme.name}
            </option>
          ))}
        </select>
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <ThemeSwatch theme={effectiveTheme} />
        {setThemeMutation.isPending && (
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>Applying…</span>
        )}
      </div>
    </div>
  );
}

function ThemeSwatch({ theme }: { theme: ReturnType<typeof useBuilderStore.getState>["formTheme"] | null }): JSX.Element {
  if (!theme) {
    return (
      <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
        Using team default styling.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <span style={{ fontSize: "0.8rem" }}>{theme.name}</span>
      <div style={{ display: "flex", gap: "0.35rem" }}>
        <ColorDot color={theme.settings.palette.primary} label="Primary" />
        <ColorDot color={theme.settings.palette.accent} label="Accent" />
        <ColorDot color={theme.settings.palette.surface} label="Surface" />
        <ColorDot color={theme.settings.palette.text} label="Text" />
      </div>
    </div>
  );
}

function ColorDot({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <div title={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.2rem" }}>
      <span
        style={{
          width: "1.1rem",
          height: "1.1rem",
          borderRadius: "999px",
          border: "1px solid rgba(0,0,0,0.08)",
          background: color,
        }}
      />
    </div>
  );
}

function FieldProperties({ field }: { field: FormFieldDefinition }): JSX.Element {
  const updateField = useBuilderStore((state) => state.updateField);

  const baseControls = (
    <>
      <FieldLabel
        label="Label"
        element={
          <input
            value={field.title}
            onChange={(event) => {
              const sanitized = sanitizeInput(event.target.value);
              const validation = validateFieldTitle(sanitized);
              if (validation.valid) {
                updateField(field.id, { title: sanitized });
              } else {
                // Still update but log warning
                console.warn(`[PropertiesPanel] ${validation.error}`);
                updateField(field.id, { title: sanitized });
              }
            }}
            style={INPUT_STYLE}
            placeholder={`${fieldTypeLabel[field.type] ?? "Field"} question`}
            maxLength={200}
          />
        }
      />
      {field.type !== "static_text" && (
        <FieldLabel
          label="Description"
          element={
            <textarea
              value={field.description ?? ""}
              onChange={(event) => {
                const sanitized = sanitizeInput(event.target.value);
                const validation = validateFieldDescription(sanitized);
                if (validation.valid) {
                  updateField(field.id, { description: sanitized || undefined });
                } else {
                  console.warn(`[PropertiesPanel] ${validation.error}`);
                  updateField(field.id, { description: sanitized || undefined });
                }
              }}
              style={TEXTAREA_STYLE}
              rows={3}
              maxLength={1000}
            />
          }
        />
      )}
      {field.type !== "static_text" && field.type !== "calculated" && (
        <FieldLabel
          label="Required"
          element={
            <input
              type="checkbox"
              checked={field.required ?? false}
              onChange={(event) => updateField(field.id, { required: event.target.checked })}
            />
          }
        />
      )}
      {field.type !== "static_text" && field.type !== "calculated" && (
        <FieldLabel
          label="Validation Message"
          element={
            <input
              value={field.validationMessage ?? ""}
              onChange={(event) =>
                updateField(field.id, {
                  validationMessage: event.target.value || undefined
                })
              }
              style={INPUT_STYLE}
              placeholder="Custom error message (optional)"
              maxLength={200}
            />
          }
        />
      )}
    </>
  );

  const advancedControls = renderTypeSpecificControls(field, updateField);

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <header>
        <h3 style={{ marginBottom: "0.35rem" }}>{fieldTypeLabel[field.type] ?? field.type}</h3>
        <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          Tune how this question behaves and validates responses.
        </p>
      </header>
      {baseControls}
      {advancedControls}
    </div>
  );
}

function SectionProperties({ section }: { section: FormSectionDefinition }): JSX.Element {
  const updateSection = useBuilderStore((state) => state.updateSection);
  const removeSection = useBuilderStore((state) => state.removeSection);
  const selectSection = useBuilderStore((state) => state.selectSection);

  const handleRemoveSection = useCallback(() => {
    if (window.confirm(`Delete "${section.title || "this section"}"? Fields will be moved to the intro area.`)) {
      removeSection(section.id);
      selectSection(null);
    }
  }, [section.id, section.title, removeSection, selectSection]);

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <header>
        <h3 style={{ marginBottom: "0.35rem" }}>Section</h3>
        <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          Sections create logical chapters and power branching rules.
        </p>
      </header>
      <FieldLabel
        label="Title"
        element={
          <input
            value={section.title}
            onChange={(event) => updateSection(section.id, { title: event.target.value })}
            style={INPUT_STYLE}
          />
        }
      />
      <FieldLabel
        label="Description"
        element={
          <textarea
            value={section.description ?? ""}
            onChange={(event) => updateSection(section.id, { description: event.target.value })}
            style={TEXTAREA_STYLE}
            rows={3}
          />
        }
      />
      <FieldLabel
        label="Layout"
        element={
          <select
            value={section.layout ?? "single_column"}
            onChange={(event) =>
              updateSection(section.id, {
                layout: event.target.value as FormSectionDefinition["layout"],
              })
            }
            style={INPUT_STYLE}
          >
            <option value="single_column">Single column</option>
            <option value="two_column">Two column</option>
          </select>
        }
      />
      <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "1rem", marginTop: "0.5rem" }}>
        <button
          type="button"
          onClick={handleRemoveSection}
          style={{
            background: "var(--color-error-bg, #fee)",
            color: "var(--color-error, #c33)",
            border: "1px solid var(--color-error, #c33)",
            borderRadius: "var(--radius-base)",
            padding: "0.6rem 1rem",
            cursor: "pointer",
            fontWeight: 500,
            width: "100%",
          }}
        >
          Remove Section
        </button>
      </div>
    </div>
  );
}

/**
 * Type guard for text field types
 */
function isTextField(field: FormFieldDefinition): field is FormFieldDefinition & { placeholder?: string } {
  return field.type === "short_text" || field.type === "long_text";
}

/**
 * Type guard for number field
 */
function isNumberField(field: FormFieldDefinition): field is FormFieldDefinition & {
  validation?: { min?: number; max?: number; step?: number };
} {
  return field.type === "number";
}

/**
 * Type guard for choice field types
 */
function isChoiceField(field: FormFieldDefinition): field is ChoiceFieldDefinition {
  return field.type === "multiple_choice" || field.type === "checkboxes";
}

/**
 * Type guard for dropdown field
 */
function isDropdownField(field: FormFieldDefinition): field is DropdownFieldDefinition {
  return field.type === "dropdown";
}

/**
 * Type guard for file upload field
 */
function isFileUploadField(field: FormFieldDefinition): field is FormFieldDefinition & {
  maxFileSizeMB?: number;
  maxFiles?: number;
  allowedTypes?: string[];
} {
  return field.type === "file_upload";
}

/**
 * Type guard for rating field
 */
function isRatingField(field: FormFieldDefinition): field is FormFieldDefinition & {
  style?: "stars" | "numbers" | "emojis";
  scale?: number;
  lowLabel?: string;
  highLabel?: string;
} {
  return field.type === "rating";
}

/**
 * Type guard for calculated field
 */
function isCalculatedField(field: FormFieldDefinition): field is CalculatedFieldDefinition {
  return field.type === "calculated";
}

/**
 * Validate field configuration
 */
function validateFieldConfig(field: FormFieldDefinition): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!field.id || typeof field.id !== "string") {
    errors.push("Field ID is required");
  }

  if (!field.title || typeof field.title !== "string" || field.title.trim().length === 0) {
    errors.push("Field title is required");
  }

  if (typeof field.position !== "number" || field.position < 0) {
    errors.push("Field position must be a non-negative number");
  }

  // Type-specific validation
  if (isNumberField(field)) {
    if (field.validation?.min !== undefined && field.validation?.max !== undefined) {
      if (field.validation.min > field.validation.max) {
        errors.push("Minimum value cannot be greater than maximum value");
      }
    }
  }

  if (isFileUploadField(field)) {
    if (field.maxFileSizeMB !== undefined) {
      if (field.maxFileSizeMB < 1 || field.maxFileSizeMB > 50) {
        errors.push("Max file size must be between 1 and 50 MB");
      }
    }
  }

  if (isRatingField(field)) {
    if (field.scale !== undefined) {
      if (field.scale < 2 || field.scale > 10) {
        errors.push("Rating scale must be between 2 and 10");
      }
    }
  }

  if (isChoiceField(field)) {
    if (!field.options || !Array.isArray(field.options)) {
      errors.push("Choice field must have options array");
    } else if (field.options.length === 0) {
      errors.push("Choice field must have at least one option");
    }
  }

  if (isCalculatedField(field)) {
    if (!field.expression || typeof field.expression !== "string" || field.expression.trim().length === 0) {
      errors.push("Calculated field must have an expression");
    }
    if (!field.inputFieldIds || !Array.isArray(field.inputFieldIds)) {
      errors.push("Calculated field must have inputFieldIds array");
    }
  }

  return { valid: errors.length === 0, errors };
}

function renderTypeSpecificControls(
  field: FormFieldDefinition,
  updateField: (fieldId: string, updates: Partial<FormFieldDefinition>) => void
): JSX.Element | null {
  // Validate field configuration before rendering
  const validation = validateFieldConfig(field);
  if (!validation.valid && validation.errors.length > 0) {
    console.warn(`[PropertiesPanel] Field ${field.id} has validation errors:`, validation.errors);
  }

  switch (field.type) {
    case "short_text":
    case "long_text": {
      if (!isTextField(field)) return null;
      return (
        <FieldLabel
          label="Placeholder"
          element={
            <input
              value={field.placeholder ?? ""}
              onChange={(event) => updateField(field.id, { placeholder: event.target.value })}
              style={INPUT_STYLE}
              placeholder="Optional placeholder text"
            />
          }
        />
      );
    }
    case "number": {
      if (!isNumberField(field)) return null;
      return (
        <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(2, 1fr)" }}>
          <FieldLabel
            label="Minimum"
            element={
              <input
                type="number"
                value={(field.validation?.min ?? "") as number | string}
                onChange={(event) => {
                  const minValue = event.target.value ? Number(event.target.value) : undefined;
                  const validation = validateNumberValidation(
                    minValue,
                    field.validation?.max
                  );
                  if (validation.valid) {
                    updateField(field.id, {
                      validation: {
                        ...field.validation,
                        min: minValue,
                      },
                    });
                  } else {
                    console.warn(`[PropertiesPanel] ${validation.error}`);
                  }
                }}
                style={INPUT_STYLE}
              />
            }
          />
          <FieldLabel
            label="Maximum"
            element={
              <input
                type="number"
                value={(field.validation?.max ?? "") as number | string}
                onChange={(event) => {
                  const maxValue = event.target.value ? Number(event.target.value) : undefined;
                  const validation = validateNumberValidation(
                    field.validation?.min,
                    maxValue
                  );
                  if (validation.valid) {
                    updateField(field.id, {
                      validation: {
                        ...field.validation,
                        max: maxValue,
                      },
                    });
                  } else {
                    console.warn(`[PropertiesPanel] ${validation.error}`);
                  }
                }}
                style={INPUT_STYLE}
              />
            }
          />
        </div>
      );
    }
    case "multiple_choice":
    case "checkboxes": {
      if (!isChoiceField(field)) return null;
      return <ChoiceOptions field={field} onChange={updateField} />;
    }
    case "dropdown": {
      if (!isDropdownField(field)) return null;
      return <DropdownOptions field={field} onChange={updateField} />;
    }
    case "file_upload": {
      if (!isFileUploadField(field)) return null;
      return <FileUploadFieldControls field={field} onChange={updateField} />;
    }
    case "static_text": {
      return <StaticTextFieldControls field={field} onChange={updateField} />;
    }

    case "rating": {
      if (!isRatingField(field)) return null;
      return <RatingFieldControls field={field} onChange={updateField} />;
    }
    case "calculated": {
      if (!isCalculatedField(field)) return null;
      return <CalculatedFieldControls field={field} onChange={updateField} />;
    }
    default:
      return null;
  }
}

function ChoiceOptions({
  field,
  onChange,
}: {
  field: ChoiceFieldDefinition;
  onChange(fieldId: string, updates: Partial<FormFieldDefinition>): void;
}): JSX.Element {
  const options = field.options ?? [];
  const schema = useBuilderStore((state) => state.schema);
  const sections = schema.sections;

  const updateOptions = (next: ChoiceFieldDefinition["options"]) =>
    onChange(field.id, { options: next });

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>Options</strong>
        <button
          type="button"
          className="button secondary"
          onClick={() =>
            updateOptions([...options, { id: `opt_${nanoid(6)}`, label: `Option ${options.length + 1}` }])
          }
        >
          Add option
        </button>
      </div>
      {options.length === 0 ? (
        <p style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>No options yet.</p>
      ) : (
        options.map((option, index) => (
          <div key={option.id} style={{ display: "grid", gap: "0.5rem" }}>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                value={option.label}
                onChange={(event) => {
                  const sanitized = sanitizeInput(event.target.value);
                  const next = [...options];
                  next[index] = { ...option, label: sanitized };
                  const validation = validateChoiceOptions(next);
                  if (validation.valid) {
                    updateOptions(next);
                  } else {
                    console.warn(`[PropertiesPanel] ${validation.error}`);
                    // Still update but log warning
                    updateOptions(next);
                  }
                }}
                style={{ ...INPUT_STYLE, flex: 1 }}
                maxLength={200}
              />
              <button
                type="button"
                className="button ghost"
                onClick={() => updateOptions(options.filter((item) => item.id !== option.id))}
              >
                Remove
              </button>
            </div>
            {/* Section Navigation Dropdown */}
            {sections.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", paddingLeft: "0.5rem" }}>
                <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
                  Go to:
                </span>
                <select
                  value={option.nextSectionId ?? ""}
                  onChange={(e) => {
                    const next = [...options];
                    next[index] = {
                      ...option,
                      nextSectionId: e.target.value || undefined
                    };
                    updateOptions(next);
                  }}
                  style={{
                    ...INPUT_STYLE,
                    flex: 1,
                    fontSize: "0.8rem",
                    padding: "0.35rem 0.5rem",
                  }}
                >
                  <option value="">Continue normally</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      📑 {section.title || `Section ${section.position + 1}`}
                    </option>
                  ))}
                  <option value="_submit">🏁 Submit form</option>
                </select>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function DropdownOptions({
  field,
  onChange,
}: {
  field: DropdownFieldDefinition;
  onChange(fieldId: string, updates: Partial<FormFieldDefinition>): void;
}): JSX.Element {
  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <ChoiceOptions field={field as unknown as ChoiceFieldDefinition} onChange={onChange} />
      <FieldLabel
        label="Allow multiple selections"
        element={
          <input
            type="checkbox"
            checked={field.allowMultiple ?? false}
            onChange={(event) =>
              onChange(field.id, {
                allowMultiple: event.target.checked,
              })
            }
          />
        }
      />
    </div>
  );
}

// File type presets for multi-select
const FILE_TYPE_PRESETS = [
  { id: "images", label: "Images", types: ["image/*"] },
  { id: "pdf", label: "PDFs", types: [".pdf", "application/pdf"] },
  { id: "ppt", label: "PowerPoint", types: [".ppt", ".pptx", "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"] },
  { id: "docs", label: "Documents", types: [".doc", ".docx", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"] },
  { id: "excel", label: "CSV/Excel", types: [".csv", ".xls", ".xlsx", "text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"] },
  { id: "archives", label: "Zip/RAR", types: [".zip", ".rar", ".7z", "application/zip", "application/x-rar-compressed"] },
];

function getSelectedPresets(allowedTypes: string[] | undefined): Set<string> {
  if (!allowedTypes || allowedTypes.length === 0) return new Set();
  const selected = new Set<string>();
  for (const preset of FILE_TYPE_PRESETS) {
    if (preset.types.some((t) => allowedTypes.includes(t))) {
      selected.add(preset.id);
    }
  }
  return selected;
}

function FileUploadFieldControls({
  field,
  onChange,
}: {
  field: FormFieldDefinition & {
    maxFileSizeMB?: number;
    maxFiles?: number;
    allowedTypes?: string[];
  };
  onChange(fieldId: string, updates: Partial<FormFieldDefinition>): void;
}): JSX.Element {
  const selectedPresets = getSelectedPresets(field.allowedTypes);

  const togglePreset = (presetId: string, checked: boolean) => {
    const preset = FILE_TYPE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;

    let newTypes = [...(field.allowedTypes ?? [])];
    if (checked) {
      // Add preset types
      for (const type of preset.types) {
        if (!newTypes.includes(type)) {
          newTypes.push(type);
        }
      }
    } else {
      // Remove preset types
      newTypes = newTypes.filter((t) => !preset.types.includes(t));
    }
    onChange(field.id, { allowedTypes: newTypes.length > 0 ? newTypes : undefined });
  };

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <FieldLabel
        label="Max file size (MB)"
        element={
          <input
            type="number"
            min={1}
            max={50}
            value={field.maxFileSizeMB ?? 10}
            onChange={(event) => {
              const size = Number(event.target.value);
              if (validateFileSizeLimit(size).valid) {
                onChange(field.id, { maxFileSizeMB: size });
              }
            }}
            style={INPUT_STYLE}
          />
        }
      />
      <FieldLabel
        label="Max files"
        element={
          <input
            type="number"
            min={1}
            value={field.maxFiles ?? 1}
            onChange={(event) => onChange(field.id, { maxFiles: Number(event.target.value) || 1 })}
            style={INPUT_STYLE}
          />
        }
      />
      <div>
        <p style={{ margin: "0 0 0.5rem", fontWeight: 500, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
          Allowed file types
        </p>
        <div style={{ display: "grid", gap: "0.35rem" }}>
          {FILE_TYPE_PRESETS.map((preset) => (
            <label key={preset.id} style={{ display: "flex", gap: "0.5rem", fontSize: "0.85rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={selectedPresets.has(preset.id)}
                onChange={(event) => togglePreset(preset.id, event.target.checked)}
              />
              {preset.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function StaticTextFieldControls({
  field,
  onChange,
}: {
  field: FormFieldDefinition;
  onChange(fieldId: string, updates: Partial<FormFieldDefinition>): void;
}): JSX.Element {
  const staticField = field as FormFieldDefinition & { content?: string; format?: string };

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <FieldLabel
        label="Content"
        element={
          <textarea
            value={staticField.content ?? ""}
            onChange={(event) => onChange(field.id, { content: event.target.value })}
            style={TEXTAREA_STYLE}
            rows={6}
            placeholder="Enter content (supports markdown)"
          />
        }
      />
      <FieldLabel
        label="Format"
        element={
          <select
            value={staticField.format ?? "markdown"}
            onChange={(event) => onChange(field.id, { format: event.target.value as "markdown" | "plain" | "html" })}
            style={INPUT_STYLE}
          >
            <option value="markdown">Markdown</option>
            <option value="plain">Plain Text</option>
            <option value="html">HTML</option>
          </select>
        }
      />
    </div>
  );
}

function RatingFieldControls({
  field,
  onChange,
}: {
  field: FormFieldDefinition & {
    style?: "stars" | "numbers" | "emojis";
    scale?: number;
    lowLabel?: string;
    highLabel?: string;
  };
  onChange(fieldId: string, updates: Partial<FormFieldDefinition>): void;
}): JSX.Element {
  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <FieldLabel
        label="Style"
        element={
          <select
            value={field.style ?? "stars"}
            onChange={(event) => onChange(field.id, { style: event.target.value as "stars" | "numbers" | "emojis" })}
            style={INPUT_STYLE}
          >
            <option value="stars">Stars</option>
            <option value="numbers">Numbers</option>
            <option value="emojis">Emojis</option>
          </select>
        }
      />
      <FieldLabel
        label="Scale (2-10)"
        element={
          <input
            type="number"
            min={2}
            max={10}
            value={field.scale ?? 5}
            onChange={(event) => {
              const scale = Number(event.target.value);
              if (validateRatingScale(scale).valid) {
                onChange(field.id, { scale });
              }
            }}
            style={INPUT_STYLE}
          />
        }
      />
      <FieldLabel
        label="Low label"
        element={
          <input
            value={field.lowLabel ?? ""}
            onChange={(event) => onChange(field.id, { lowLabel: event.target.value || undefined })}
            style={INPUT_STYLE}
            placeholder="e.g., Poor"
          />
        }
      />
      <FieldLabel
        label="High label"
        element={
          <input
            value={field.highLabel ?? ""}
            onChange={(event) => onChange(field.id, { highLabel: event.target.value || undefined })}
            style={INPUT_STYLE}
            placeholder="e.g., Excellent"
          />
        }
      />
    </div>
  );
}


function CalculatedFieldControls({
  field,
  onChange,
}: {
  field: CalculatedFieldDefinition;
  onChange(fieldId: string, updates: Partial<FormFieldDefinition>): void;
}): JSX.Element {
  const schema = useBuilderStore((state) => state.schema);
  const selectableFields = schema.fields.filter((candidate) => candidate.id !== field.id);
  const updateInputs = (fieldId: string, checked: boolean) => {
    const set = new Set(field.inputFieldIds);
    if (checked) set.add(fieldId);
    else set.delete(fieldId);
    onChange(field.id, { inputFieldIds: Array.from(set) });
  };

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <FieldLabel
        label="Expression"
        element={
          <textarea
            value={field.expression}
            onChange={(event) => onChange(field.id, { expression: event.target.value })}
            style={TEXTAREA_STYLE}
            rows={4}
            placeholder='Example: (answer("rating") / 5) * 100'
          />
        }
      />
      <FieldLabel
        label="Output type"
        element={
          <select
            value={field.outputType}
            onChange={(event) =>
              onChange(field.id, {
                outputType: event.target.value as CalculatedFieldDefinition["outputType"],
              })
            }
            style={INPUT_STYLE}
          >
            <option value="number">Number</option>
            <option value="text">Text</option>
            <option value="boolean">Boolean</option>
            <option value="date">Date</option>
          </select>
        }
      />
      <div>
        <p style={{ margin: "0 0 0.3rem", fontWeight: 600 }}>Referenced fields</p>
        <div style={{ display: "grid", gap: "0.35rem" }}>
          {selectableFields.map((candidate) => (
            <label key={candidate.id} style={{ display: "flex", gap: "0.5rem", fontSize: "0.85rem" }}>
              <input
                type="checkbox"
                checked={field.inputFieldIds.includes(candidate.id)}
                onChange={(event) => updateInputs(candidate.id, event.target.checked)}
              />
              {candidate.title || candidate.id}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({
  label,
  element,
}: {
  label: string;
  element: JSX.Element;
}): JSX.Element {
  return (
    <label style={LABEL_STYLE}>
      <span>{label}</span>
      {element}
    </label>
  );
}

function Placeholder({ message }: { message: string }): JSX.Element {
  return (
    <div style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
      {message}
    </div>
  );
}

const LABEL_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.35rem",
  fontSize: "0.85rem",
  color: "var(--color-text-muted)",
  fontWeight: 500,
};

const INPUT_STYLE: React.CSSProperties = {
  borderRadius: "10px",
  border: "1px solid var(--color-border)",
  padding: "0.55rem 0.75rem",
  fontSize: "0.9rem",
  outline: "none",
};

const TEXTAREA_STYLE: React.CSSProperties = {
  ...INPUT_STYLE,
  minHeight: "70px",
  resize: "vertical",
};

export default PropertiesPanel;
